import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { buildOrderDragReorder } from '../../lib/projects/buildOrderDragReorder';
import {
  DisclosureChevron,
  EmptyState,
  ProgressBar,
  RecordCard,
  SectionHead
} from '../primitives';
import {
  projectBuildOrderTreeView,
  type BuildOrderTreeRow
} from '../../lib/projects/projectBuildOrderView';
import type { ProjectWorkItem, WorkItemDependencyRow } from './types';

/** How long the transient "Updated by the agent" cue stays before it fades. */
const AGENT_CUE_MS = 3200;

/** A shared empty id set — a stable identity for "no agent-moved rows". */
const EMPTY_IDS: ReadonlySet<string> = new Set<string>();

/**
 * The ids that changed position between two id orders — the rows an externally-
 * arrived reorder (the agent channel) moved. An id is "moved" when its index in
 * `next` differs from its index in `prev`. Ids absent from either order
 * (added/removed by a concurrent edit) are not treated as moved.
 */
function movedIds(
  prev: ReadonlyArray<string>,
  next: ReadonlyArray<string>
): ReadonlySet<string> {
  const prevIndex = new Map<string, number>();
  prev.forEach((id, i) => prevIndex.set(id, i));
  const moved = new Set<string>();
  next.forEach((id, i) => {
    const was = prevIndex.get(id);
    if (was !== undefined && was !== i) moved.add(id);
  });
  return moved;
}

/**
 * Persist a reordered SIBLING GROUP — `parentId` names the group (`null` = the
 * project's top level: epics + orphan items) and `sequence` is the group's FULL
 * id permutation in the new order. Writes ONLY `work_items.build_order`.
 * Returns true on success (incl. a no-op), false on a write failure (the
 * section then reverts to the last persisted order). When omitted, the section
 * renders read-only — no drag handle, no reorder.
 */
export type ProjectBuildOrderReorder = (
  parentId: string | null,
  sequence: ReadonlyArray<string>
) => Promise<boolean>;

export interface ProjectBuildOrderSectionProps {
  /** The open project's work items (carrying `build_order` + `rank` + the tree shape). */
  items: ReadonlyArray<ProjectWorkItem>;
  /** The open project's `work_item_dependencies` ("blocked by") edges. */
  dependencies: ReadonlyArray<WorkItemDependencyRow>;
  /**
   * Persist one sibling group's new order through the ungated path. When
   * provided, the section becomes **drag-reorderable**: an epic card (or orphan
   * top-level row) re-sequences the top-level group; a child row re-sequences
   * its own group. A drop across groups is a no-op (no cross-epic drag — that
   * would silently re-parent). When absent, the section is read-only.
   */
  onReorder?: ProjectBuildOrderReorder;
}

/** The one in-flight optimistic group order (a drag awaiting its reconcile). */
interface OptimisticGroup {
  parentId: string | null;
  ids: ReadonlyArray<string>;
}

/**
 * The live **Build Order** section — 05-project-hub-build-order.
 *
 * Leads the project page (mounted FIRST on `ProjectCanvas.tsx`, above Epics): it
 * answers "in what order will this project be built?" at a glance. The section
 * renders the per-sibling build-order TREE (a re-derived **view** over
 * `projectBuildOrderTreeView`, never a second source of truth):
 *   - **Epic cards** — expandable (default open), interleaved with orphan
 *     top-level items in the top-level sibling order; each card lists its child
 *     work items (and their subtasks, indented) in the children's own sibling
 *     order, positions restarting at 1 per group.
 *   - **Blockedness is inline** — a blocked row keeps its position and carries
 *     "Blocked by [item name]" beside its name. Surfaced, never hidden, and a
 *     contradiction (blocked row ordered first) is shown, never prevented.
 *   - **Next up** — the single actionable head of the walk (`nextUpId`) carries
 *     a marker; it is the SAME item the agent names as the next step.
 *
 * **Drag-to-reorder (when `onReorder` is set).** ONE `DndContext`, one
 * `SortableContext` per sibling group: dragging an epic card re-sequences the
 * top-level group; dragging a child row re-sequences its own epic's children. A
 * drop across groups is a NO-OP — enforced in the drop handler, the single
 * cross-epic gate. Each node carries a **visible drag handle** and reorder is
 * keyboard-accessible, via the repo's existing `@dnd-kit/sortable`. On drop:
 *   - apply the new GROUP order **optimistically** (no approval card);
 *   - call the ungated `onReorder(parentId, sequence)` — writes ONLY
 *     `work_items.build_order` for that group;
 *   - on a write failure, **revert** to the pre-drag snapshot and surface the
 *     error;
 *   - the surface's existing `work_items` realtime `reload()` re-feeds props and
 *     reconciles to the canonical order — so manual drag and the agent-live
 *     channel interleave without clobbering.
 *
 * **Agent cue.** An externally-arrived reorder (NOT the person's own drag)
 * marks the rows whose position in the DFS walk (`flatWalkIds`) changed with a
 * transient "Updated by the agent" cue — same mechanics as the flat era.
 *
 * All ordering comes from `projectBuildOrderTreeView` → `buildOrderTree.ts`;
 * the section never invents an order of its own.
 */
export function ProjectBuildOrderSection({
  items,
  dependencies,
  onReorder
}: ProjectBuildOrderSectionProps) {
  const draggable = typeof onReorder === 'function';

  // The view derived straight from props — the canonical, reconciled order.
  const propView = useMemo(
    () => projectBuildOrderTreeView(items, dependencies),
    [items, dependencies]
  );

  // Local optimistic group order. `null` ⇒ "follow props"; a value ⇒ the ONE
  // sibling group the person just dragged, awaiting reconcile. Props changing
  // (the realtime reload, or an agent-live reorder) clears it back to `null`.
  const [optimistic, setOptimistic] = useState<OptimisticGroup | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The "Updated by the agent" cue. When a reorder arrives from props that the
  // section did NOT initiate itself, mark the rows that moved in the walk and
  // fade the marker. A person's own drag landing carries no cue.
  const [agentMovedIds, setAgentMovedIds] = useState<ReadonlySet<string>>(EMPTY_IDS);
  const lastRenderedOrderRef = useRef<ReadonlyArray<string> | null>(null);
  const selfReorderRef = useRef(false);
  const cueTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // When the canonical inputs change (reload / agent-live / blocker done),
  // discard the optimistic order — props are the source of truth — and decide
  // whether the new walk is an AGENT-driven reorder (→ cue) or the person's own
  // drag reconciling (→ no cue). The diff runs over the PROPS walk, never the
  // optimistic one.
  useEffect(() => {
    setOptimistic(null);

    const prev = lastRenderedOrderRef.current;
    lastRenderedOrderRef.current = propView.flatWalkIds;

    if (selfReorderRef.current) {
      selfReorderRef.current = false;
      return;
    }
    if (!prev) return;

    const moved = movedIds(prev, propView.flatWalkIds);
    if (moved.size === 0) return;

    setAgentMovedIds(moved);
    if (cueTimerRef.current) clearTimeout(cueTimerRef.current);
    cueTimerRef.current = setTimeout(() => {
      setAgentMovedIds(EMPTY_IDS);
      cueTimerRef.current = null;
    }, AGENT_CUE_MS);
    // propView carries the props identity we diff on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, dependencies]);

  // Clear any pending cue timer on unmount.
  useEffect(
    () => () => {
      if (cueTimerRef.current) clearTimeout(cueTimerRef.current);
    },
    []
  );

  // The displayed tree — the canonical view with the one optimistic group
  // re-sequenced (positions re-derived for that group only).
  const topLevel = useMemo(
    () => (optimistic ? applyGroupOrder(propView.topLevel, optimistic) : propView.topLevel),
    [propView, optimistic]
  );

  // Sibling-group membership (id → parentId) — the drop handler's single
  // cross-group gate reads this, derived from the canonical view.
  const groupByItem = useMemo(() => {
    const map = new Map<string, string | null>();
    const walk = (rows: ReadonlyArray<BuildOrderTreeRow>) => {
      for (const row of rows) {
        map.set(row.id, row.parentId);
        walk(row.children);
      }
    };
    walk(propView.topLevel);
    return map;
  }, [propView]);

  const total = items.length;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const activeId = String(event.active.id);
      const overId = event.over ? String(event.over.id) : null;
      if (!overId) return;

      // ONE gate for cross-epic drags: active and over must share a sibling
      // group, else the drop is a no-op (a cross-group move would re-parent,
      // which a reorder must never do).
      const parentId = groupByItem.get(activeId);
      if (parentId === undefined) return;
      if (groupByItem.get(overId) !== parentId) return;

      // The group's CURRENTLY DISPLAYED order is the drag's baseline (and the
      // revert snapshot on a write failure).
      const current = collectGroupIds(topLevel, parentId);
      const next = buildOrderDragReorder(current, activeId, overId);
      if (!next) return;

      // Optimistic: show the new order at once, no approval card, no confirm
      // gate. Mark the reconcile that follows as self-initiated (NO agent cue).
      selfReorderRef.current = true;
      setOptimistic({ parentId, ids: next });
      setError(null);

      void (async () => {
        try {
          const ok = await onReorder?.(parentId, next);
          if (ok === false) {
            setOptimistic({ parentId, ids: current });
            setError('Couldn’t save the new order. Reverted to the last saved order.');
          }
          // On success, the surface's realtime reload re-feeds props → the
          // `useEffect` clears the optimistic order → reconcile to canonical.
        } catch {
          setOptimistic({ parentId, ids: current });
          setError('Couldn’t save the new order. Reverted to the last saved order.');
        }
      })();
    },
    [groupByItem, onReorder, topLevel]
  );

  const tree = (
    <div className="pn-buildorder">
      <SiblingGroupList
        rows={topLevel}
        draggable={draggable}
        agentMovedIds={agentMovedIds}
        depth={0}
      />
    </div>
  );

  const body =
    total === 0 ? (
      <EmptyState data-testid="project-build-order-empty">
        No work items in this project yet — the build order appears here once it has work.
      </EmptyState>
    ) : draggable ? (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        {tree}
      </DndContext>
    ) : (
      tree
    );

  return (
    <section className="pn-section" data-testid="project-build-order-section">
      <SectionHead title="Build Order" tag={String(total)} />
      {error ? (
        <div className="pn-buildorder-error" role="alert" data-testid="project-build-order-error">
          {error}
        </div>
      ) : null}
      {body}
    </section>
  );
}

/**
 * One sibling group: a `SortableContext` (when draggable) over the group's rows.
 * Every group — the top level, an epic's children, a story's subtasks — renders
 * through here, so each gets its OWN sortable sequence under the section's one
 * `DndContext`.
 */
function SiblingGroupList({
  rows,
  draggable,
  agentMovedIds,
  depth
}: {
  rows: ReadonlyArray<BuildOrderTreeRow>;
  draggable: boolean;
  agentMovedIds: ReadonlySet<string>;
  depth: number;
}) {
  if (rows.length === 0) return null;
  const list = (
    <ul className="pn-buildorder-list" data-depth={depth}>
      {rows.map((row) => (
        <BuildOrderNode
          key={row.id}
          row={row}
          draggable={draggable}
          agentMovedIds={agentMovedIds}
          depth={depth}
        />
      ))}
    </ul>
  );
  if (!draggable) return list;
  return (
    <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
      {list}
    </SortableContext>
  );
}

/** One tree node: an expandable epic card, or an item row (with nested subtasks). */
function BuildOrderNode({
  row,
  draggable,
  agentMovedIds,
  depth
}: {
  row: BuildOrderTreeRow;
  draggable: boolean;
  agentMovedIds: ReadonlySet<string>;
  depth: number;
}) {
  const sortable = useSortable({ id: row.id, disabled: !draggable });
  const [open, setOpen] = useState(true);
  const agentMoved = agentMovedIds.has(row.id);

  const sortableProps = {
    ref: draggable ? sortable.setNodeRef : undefined,
    style: draggable
      ? { transform: CSS.Transform.toString(sortable.transform), transition: sortable.transition }
      : undefined
  };

  const handle = draggable ? (
    <RecordCard.DragRail
      className="pn-buildorder-handle"
      aria-label={`Reorder ${row.name}`}
      dragging={sortable.isDragging}
      data-testid={`project-build-order-handle-${row.id}`}
      {...sortable.listeners}
      {...sortable.attributes}
    />
  ) : null;

  const blockedNote =
    row.blockedByNames.length > 0 ? (
      <span className="pn-buildorder-blockedby">{blockedByLabel(row.blockedByNames)}</span>
    ) : null;

  const agentCue = agentMoved ? (
    <span
      className="pn-buildorder-agentcue"
      data-testid={`project-build-order-agent-cue-${row.id}`}
    >
      Updated by the agent
    </span>
  ) : null;

  if (row.kind === 'epic') {
    const done = row.children.filter((c) => c.isCompleted).length;
    const childCount = row.children.length;
    return (
      <li
        {...sortableProps}
        className={[
          'pn-buildorder-epic',
          sortable.isDragging ? 'pn-buildorder-epic--dragging' : null,
          agentMoved ? 'pn-buildorder-row--agent-moved' : null
        ]
          .filter(Boolean)
          .join(' ')}
        data-testid={`project-build-order-row-${row.id}`}
        data-kind="epic"
        data-open={open ? '1' : '0'}
        data-dragging={sortable.isDragging ? 'true' : undefined}
        data-agent-moved={agentMoved ? 'true' : undefined}
      >
        <div className="pn-buildorder-epic-row">
          {handle}
          <DisclosureChevron
            expanded={open}
            aria-label={open ? `Collapse ${row.name}` : `Expand ${row.name}`}
            data-testid={`project-build-order-epic-toggle-${row.id}`}
            onClick={() => setOpen((v) => !v)}
          />
          <span className="pn-buildorder-pos" aria-hidden="true">
            {row.position}
          </span>
          <div className="pn-buildorder-epic-main">
            <span className="pn-buildorder-epic-name">{row.name}</span>
            <span className="pn-buildorder-epic-sub">
              {childCount} work item{childCount === 1 ? '' : 's'} · {done} done
            </span>
          </div>
          {blockedNote}
          {agentCue}
          <ProgressBar value={done} total={childCount} label={`${row.name} progress`} size="sm" />
        </div>
        {open ? (
          <div className="pn-buildorder-epic-body">
            {childCount === 0 ? (
              <div className="pn-buildorder-epic-empty">No work items in this epic yet.</div>
            ) : (
              <SiblingGroupList
                rows={row.children}
                draggable={draggable}
                agentMovedIds={agentMovedIds}
                depth={depth + 1}
              />
            )}
          </div>
        ) : null}
      </li>
    );
  }

  return (
    <li
      {...sortableProps}
      className={[
        'pn-buildorder-node',
        sortable.isDragging ? 'pn-buildorder-node--dragging' : null
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid={`project-build-order-row-${row.id}`}
      data-kind="item"
      data-dragging={sortable.isDragging ? 'true' : undefined}
      data-agent-moved={agentMoved ? 'true' : undefined}
    >
      <div
        className={[
          'pn-buildorder-row',
          row.blockedByNames.length > 0 ? 'pn-buildorder-row--blocked' : null,
          row.isCompleted ? 'pn-buildorder-row--done' : null,
          draggable ? 'pn-buildorder-row--draggable' : null,
          sortable.isDragging ? 'pn-buildorder-row--dragging' : null,
          agentMoved ? 'pn-buildorder-row--agent-moved' : null
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {handle}
        <span className="pn-buildorder-pos" aria-hidden="true">
          {row.position}
        </span>
        <span className="pn-buildorder-name">{row.name}</span>
        {row.nextUp ? (
          <span className="pn-buildorder-next" data-testid="project-build-order-next-up">
            Next up
          </span>
        ) : null}
        {blockedNote}
        {agentCue}
      </div>
      {row.children.length > 0 ? (
        <SiblingGroupList
          rows={row.children}
          draggable={draggable}
          agentMovedIds={agentMovedIds}
          depth={depth + 1}
        />
      ) : null}
    </li>
  );
}

/**
 * Re-sequence the ONE optimistic sibling group inside the canonical tree,
 * re-deriving that group's 1-based positions; every other group passes through
 * untouched. Ids missing from the optimistic order (a concurrent edit) fall to
 * the end in canonical order — defensive, never dropped.
 */
function applyGroupOrder(
  rows: ReadonlyArray<BuildOrderTreeRow>,
  optimistic: OptimisticGroup
): BuildOrderTreeRow[] {
  const apply = (
    group: ReadonlyArray<BuildOrderTreeRow>,
    parentId: string | null
  ): BuildOrderTreeRow[] => {
    let seq: BuildOrderTreeRow[] = [...group];
    if (parentId === optimistic.parentId) {
      const byId = new Map(group.map((r) => [r.id, r]));
      const ordered = optimistic.ids
        .map((id) => byId.get(id))
        .filter((r): r is BuildOrderTreeRow => !!r);
      for (const r of group) {
        if (!ordered.includes(r)) ordered.push(r);
      }
      seq = ordered.map((r, i) => ({ ...r, position: i + 1 }));
    }
    return seq.map((r) => ({ ...r, children: apply(r.children, r.id) }));
  };
  return apply(rows, null);
}

/** The displayed id order of one sibling group (`null` = the top level). */
function collectGroupIds(
  rows: ReadonlyArray<BuildOrderTreeRow>,
  parentId: string | null
): string[] {
  if (parentId === null) return rows.map((r) => r.id);
  const stack = [...rows];
  while (stack.length > 0) {
    const row = stack.pop()!;
    if (row.id === parentId) return row.children.map((c) => c.id);
    stack.push(...row.children);
  }
  return [];
}

/** "Blocked by [item name]" — names the still-pending blocker(s). */
function blockedByLabel(names: ReadonlyArray<string>): string {
  const named = names.filter((n) => n.length > 0);
  if (named.length === 0) return 'Blocked by a prerequisite';
  return `Blocked by ${named.map((n) => `"${n}"`).join(', ')}`;
}
